import { createFileRoute, Link } from '@tanstack/react-router'
import { useScenarioResults, clearScenarioResults } from '@/hooks/useScenarioResults'
import { WaterfallChart } from '@/components/charts/WaterfallChart'
import { DrawdownChart, EmptyDrawdownChart } from '@/components/charts/DrawdownChart'
import { DeltaComparisonTable } from '@/components/tables/DeltaComparisonTable'
import { CoherenceBadge } from '@/components/results/CoherenceBadge'
import { ProxyAuditPanel } from '@/components/results/ProxyAuditPanel'
import { RiskDecompositionPanel } from '@/components/results/RiskDecompositionPanel'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Separator } from '@/components/ui/separator'
import {
  ArrowLeft,
  RotateCcw,
  Download,
  Clock,
  Database,
  Zap,
  AlertCircle,
  CheckCircle,
} from 'lucide-react'
import type { ScenarioMode } from '@/types/api'

function ResultsPage() {
  const { data: results, isLoading, error } = useScenarioResults()

  // Handle loading state
  if (isLoading) {
    return <ResultsLoadingState />
  }

  // Handle error/no results state
  if (error || !results) {
    return <ResultsEmptyState error={error?.message} />
  }

  const {
    scenario_summary,
    pnl_waterfall,
    adjustment_deltas,
    risk_decomposition,
    drawdown_path,
    coherence_diagnostic,
    proxy_audit,
  } = results

  // Format mode display
  const getModeDisplay = (mode: ScenarioMode): string => {
    switch (mode) {
      case 'HISTORICAL_REPLAY':
        return 'Historical Replay'
      case 'BTC_CURVE_REGIME':
        return 'BTC Curve + Regime'
      case 'CUSTOM_SHOCKS':
        return 'Custom Shocks'
      default:
        return mode
    }
  }

  // Format timestamp
  const formatTimestamp = (timestamp: string): string => {
    try {
      return new Date(timestamp).toLocaleString('en-US', {
        dateStyle: 'medium',
        timeStyle: 'short',
      })
    } catch {
      return timestamp
    }
  }

  const handleRunNewScenario = () => {
    clearScenarioResults()
  }

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Scenario Results</h1>
          <p className="mt-1 text-gray-600">
            {getModeDisplay(scenario_summary.mode)} • {formatTimestamp(scenario_summary.computation_timestamp)}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" asChild>
            <Link to="/scenarios">
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to Scenarios
            </Link>
          </Button>
          <Button variant="outline" size="sm" onClick={handleRunNewScenario} asChild>
            <Link to="/scenarios">
              <RotateCcw className="h-4 w-4 mr-1" />
              Run New Scenario
            </Link>
          </Button>
          <Button variant="outline" size="sm" disabled>
            <Download className="h-4 w-4 mr-1" />
            Export (v2)
          </Button>
        </div>
      </div>

      {/* Top Row: Coherence Badge, Metadata, Cache Status */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Coherence Badge */}
        <CoherenceBadge diagnostic={coherence_diagnostic} />

        {/* Scenario Metadata */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Scenario Details</CardTitle>
            <CardDescription>Execution metadata</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-500">Mode</span>
              <Badge variant="outline">{getModeDisplay(scenario_summary.mode)}</Badge>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-500">Execution Time</span>
              <span className="text-sm font-mono">
                {(scenario_summary.execution_time_ms / 1000).toFixed(2)}s
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-500">Cache Status</span>
              <Badge
                variant={scenario_summary.cache_hit ? 'default' : 'outline'}
                className={scenario_summary.cache_hit ? 'bg-emerald-100 text-emerald-700' : ''}
              >
                {scenario_summary.cache_hit ? (
                  <><CheckCircle className="h-3 w-3 mr-1" /> HIT</>
                ) : (
                  <><Zap className="h-3 w-3 mr-1" /> MISS</>
                )}
              </Badge>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-500">Classifications</span>
              <span className="text-sm font-mono">
                {scenario_summary.asset_classifications.length} assets
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Summary Stats */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Quick Stats</CardTitle>
            <CardDescription>Key metrics at a glance</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-500">Total P&L</span>
              <span className={`text-sm font-mono font-medium ${
                Number(pnl_waterfall.portfolioTotal.percentagePnL) >= 0 ? 'text-emerald-600' : 'text-red-600'
              }`}>
                {Number(pnl_waterfall.portfolioTotal.percentagePnL) >= 0 ? '+' : ''}
                {Number(pnl_waterfall.portfolioTotal.percentagePnL).toFixed(2)}%
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-500">Start NAV</span>
              <span className="text-sm font-mono">
                ${Number(pnl_waterfall.portfolioTotal.startingNAV).toLocaleString()}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-500">End NAV</span>
              <span className="text-sm font-mono">
                ${Number(pnl_waterfall.portfolioTotal.endingNAV).toLocaleString()}
              </span>
            </div>
            {drawdown_path && (
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-500">Max Drawdown</span>
                <span className="text-sm font-mono text-red-600">
                  {Number(drawdown_path.max_drawdown).toFixed(2)}%
                </span>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Second Row: Waterfall Chart (left 2/3) + Risk Decomposition (right 1/3) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          <WaterfallChart data={pnl_waterfall} height={400} />
        </div>
        <div className="lg:col-span-1">
          <RiskDecompositionPanel decomposition={risk_decomposition} />
        </div>
      </div>

      {/* Third Row: Drawdown Chart (only for Historical Replay) */}
      {scenario_summary.mode === 'HISTORICAL_REPLAY' && drawdown_path ? (
        <DrawdownChart data={drawdown_path} height={350} />
      ) : scenario_summary.mode === 'HISTORICAL_REPLAY' ? (
        <EmptyDrawdownChart reason="no-data" />
      ) : (
        <EmptyDrawdownChart reason="not-available" />
      )}

      {/* Fourth Row: Delta Comparison Table (if adjustments) */}
      {adjustment_deltas && (
        <DeltaComparisonTable
          data={adjustment_deltas}
          baselinePositions={pnl_waterfall.buckets.flatMap(b => b.positions)}
        />
      )}

      {/* Fifth Row: Proxy Audit Panel */}
      <ProxyAuditPanel auditEntries={proxy_audit} />

      {/* Footer */}
      <Separator className="my-6" />
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 text-sm text-gray-500">
        <div className="flex items-center gap-2">
          <Clock className="h-4 w-4" />
          <span>Last updated: {formatTimestamp(scenario_summary.computation_timestamp)}</span>
        </div>
        <div className="flex items-center gap-2">
          <Database className="h-4 w-4" />
          <span>Scenario ID: {scenario_summary.scenario_id}</span>
        </div>
      </div>
    </div>
  )
}

function ResultsLoadingState() {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-4 w-64 mt-2" />
        </div>
        <div className="flex gap-2">
          <Skeleton className="h-9 w-32" />
          <Skeleton className="h-9 w-32" />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Skeleton className="h-64" />
        <Skeleton className="h-64" />
        <Skeleton className="h-64" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Skeleton className="h-96 lg:col-span-2" />
        <Skeleton className="h-96" />
      </div>
    </div>
  )
}

function ResultsEmptyState({ error }: { error?: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 px-4">
      <div className="bg-amber-50 rounded-full p-4 mb-4">
        <AlertCircle className="h-10 w-10 text-amber-500" />
      </div>
      <h2 className="text-xl font-semibold text-gray-900 mb-2">
        No Scenario Results Available
      </h2>
      <p className="text-gray-600 text-center max-w-md mb-6">
        {error || 'Run a scenario first to see results. Results are stored temporarily during your session.'}
      </p>
      <Button asChild>
        <Link to="/scenarios">
          <RotateCcw className="h-4 w-4 mr-2" />
          Run a Scenario
        </Link>
      </Button>
    </div>
  )
}

export const Route = createFileRoute('/results')({
  component: ResultsPage,
})
